<template>
  <card no-body body-classes="px-0 pb-1" footer-classes="pb-2">
    <template slot="header">
      <div class="d-flex justify-content-between">
        <h3 class="mb-0">{{ title }}</h3>
        <b-button size="sm" variant="danger" @click="$emit('exportPipeline')">Export Sales Pipeline</b-button>
      </div>
    </template>
    <div>
      <b-collapse visible id="filters">
        <b-col cols="12" class="d-flex justify-content-center justify-content-sm-between flex-wrap mt-4"
        >
          <el-select
            class="select-primary pagination-select"
            v-model="filters.type"
            placeholder="Select a Type"
          >
            <el-option
              class="select-primary"
              label="All Types"
              value=""
            ></el-option>
            <el-option
              class="select-primary"
              v-for="type in types"
              :key="type.value"
              :label="type.label"
              :value="type.value"
            >
            </el-option>
          </el-select>

          <el-select
            class="select-primary pagination-select"
            v-model="filters.status"
            placeholder="Select a Status"
          >
            <el-option
              class="select-primary"
              label="All Pipelines"
              value=""
            ></el-option>
            <el-option
              class="select-primary"
              v-for="status in statuses"
              :key="status.value"
              :label="status.label"
              :value="status.value"
            >
            </el-option>
          </el-select>

          <el-select
            class="select-primary pagination-select"
            v-model="pagination.perPage"
            placeholder="Per page"
          >
            <el-option
              class="select-primary"
              v-for="item in pagination.perPageOptions"
              :key="item"
              :label="item"
              :value="item"
            >
            </el-option>
          </el-select>

          <base-input v-model="searchQuery"
            prependIcon="fas fa-search"
            placeholder="Search...">
          </base-input>

          <b-form-checkbox
            id="transferred"
            v-model="filters.transferred"
            name="transferred"
            value="1"
            unchecked-value="0"
            >
            Filter Transferred
          </b-form-checkbox>
        </b-col>
      </b-collapse>
      <b-table responsive small :items="tableData" :fields="tableColumns" :perPage="pagination.perPage" :currentPage="pagination.currentPage" :filter="searchQuery">
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>Loading...</strong>
          </div>
        </template>

        <template #cell(type)="row">
          <b-badge :variant="variants[row.item.type]">{{ row.item.type }}</b-badge>
        </template>

        <template #cell(status)="row">
          <b-badge>{{ row.item.status }}</b-badge>
        </template>

        <template #cell(primary_source)="row">
          <span v-if="row.item.primary_source">{{ row.item.primary_source }}</span>
          <span v-if="row.item.primary_source && row.item.source"> - </span>
          <span v-if="row.item.source">{{ row.item.source }}</span>
        </template>

        <template #cell(name)="row">
          {{ row.item.client.name }}
        </template>

        <template #cell(full_address)="row">
          {{ row.item.property.full_address }}
        </template>

        <template #cell(phone)="row">
          {{ row.item.client.phone }}
        </template>

        <template #cell(email)="row">
          {{ row.item.client.email }}
        </template>

        <template #cell(price)="row">
          {{ row.item.price | currency }}
        </template>

        <template #cell(potential_gci)="row">
          {{ row.item.price * (row.item.commission / 100) | currency }}
        </template>

        <template #cell(potential_profit)="row">
          {{ (row.item.price * (row.item.commission / 100) * (row.item.cos / 100)) | currency }}
        </template>

        <template #cell(pipeline_rating)="row">
          <span v-if="ratingStatuses.includes(row.item.status)">
            {{ row.item.pipeline_rating }}
          </span>
        </template>

        <template #cell(actions)="row">
          <b-dropdown text="Actions" size="sm" variant="info" boundary="viewport">
            <b-dropdown-item variant="primary" @click="handleEdit(row)" v-if="canAddEdit"><i class="fa fa-edit"></i> Edit</b-dropdown-item>
            <b-dropdown-item variant="success" @click="handleTransfer(row)" v-if="row.item.transferrable"><i class="fa fa-home"></i> Listing/Transaction</b-dropdown-item>
            <b-dropdown-item variant="danger" @click="handleDelete(row)" v-if="canAddEdit"><i class="fa fa-trash"></i> Delete</b-dropdown-item>
            <b-dropdown-item variant="info" @click="commentsModal(row)"><i class="fa fa-edit"></i> Notes</b-dropdown-item>
            <b-dropdown-item variant="warning" @click="transferPipeline(row)" v-if="agent"><i class="fa fa-exchange"></i> Transfer to Rainmaker</b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
    </div>
    <div
      slot="footer"
      class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
    >
      <div class="">
        <p class="card-category">
          Showing {{ from + 1 }} to {{ to }} of {{ total }} entries

          <span v-if="selectedRows.length">
            &nbsp; &nbsp; {{selectedRows.length}} rows selected
          </span>
        </p>

      </div>
      <base-pagination
        class="pagination-no-border"
        :current="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
        @change="paginationChanged($event)"
      >
      </base-pagination>
    </div>
    <comments-modal :type="'pipelines'"
                 :typeId="typeId"
                 :userRoute="userRoute">
    </comments-modal>
  </card>
</template>
<script>
import axios from 'axios';
import swal from 'sweetalert2';
import { Select, Option } from 'element-ui';
import { BasePagination } from '@/components';
import paginationMixin from '../Tables/PaginatedTables/paginationMixin';
import errorsMixin from '@/mixins/errors-mixin';
import CommentsModal from '@/views/Components/Modals/CommentsModal';

export default {
  mixins: [paginationMixin, errorsMixin],
  components: {
    BasePagination,
    [Select.name]: Select,
    [Option.name]: Option,
    CommentsModal
  },
  props: {
    title: null,
    data: null,
    tableColumns: null,
    propsToSearch: null,
    types: null,
    statuses: null,
    variants: null,
    canAddEdit: null,
    userRoute: null
  },
  computed: {
    roles() {
      if(this.$route.params.user) {
        return this.$store.getters['observed/observed'].roles;
      }
      return this.$store.getters['roles/roles'];
    },
    agent() {
      return this.roles.includes('agent');
    },
  },
  data() {
    return {
      tableData: this.data,
      selectedRows: [],
      searchQuery: '',
      filters: {
        type: '',
        status: '',
        transferred: false
      },
      year: new Date().getFullYear(),
      years: [
        { value: new Date().getFullYear() - 6, label: new Date().getFullYear() - 6 },
        { value: new Date().getFullYear() - 5, label: new Date().getFullYear() - 5 },
        { value: new Date().getFullYear() - 4, label: new Date().getFullYear() - 4 },
        { value: new Date().getFullYear() - 3, label: new Date().getFullYear() - 3 },
        { value: new Date().getFullYear() - 2, label: new Date().getFullYear() - 2 },
        { value: new Date().getFullYear() - 1, label: new Date().getFullYear() - 1 },
        { value: new Date().getFullYear(), label: new Date().getFullYear() },
        { value: new Date().getFullYear() + 1, label: new Date().getFullYear() + 1 },
        { value: new Date().getFullYear() + 2, label: new Date().getFullYear() + 2 },
        { value: new Date().getFullYear() + 3, label: new Date().getFullYear() + 3 },
      ],
      typeId: null,
      ratingStatuses: [
        'Nurture',
        'Appt Set',
        'Appt Held',
        'Agreement Signed',
        'Listing Taken'
      ]
    };
  },
  methods: {
    paginationChanged(page) {
      this.pagination.currentPage = page
    },
    handleEdit(row) {
      this.$router.push({
        path: this.$route.path + "/" + row.item.id,
        query: this.$route.query
      });
    },
    handleDelete(row) {
      swal.fire({
        title: 'Are you sure?',
        text: `You won't be able to revert this!`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonClass: 'btn btn-success btn-fill',
        cancelButtonClass: 'btn btn-danger btn-fill',
        confirmButtonText: 'Yes, delete it!',
        buttonsStyling: false
      }).then(result => {
        if (result.value) {
          this.deleteRow(row);
          swal.fire({
            title: 'Deleted!',
            text: `Your pipeline report has been deleted.`,
            type: 'success',
            showConfirmButton: false,
            timer: 1500,
            icon: 'success'
          });
        }
      });
    },
    async deleteRow(row) {
      await axios.delete(this.userRoute + "/pipelines/" + row.item.id)
      .then(response => {
        this.tableData.splice(row.index, 1);
      })
      .catch(error => {
        this.error(error);
      });
    },
    selectionChange(selectedRows) {
      this.selectedRows = selectedRows
    },
    commentsModal(row) {
      this.typeId = row.item.id;
      this.$bvModal.show('comments-modal');
    },
    handleTransfer(row) {
      return this.$router.push({
        path: "pipelines/" + row.item.id + "/transfer"
      });
    },
    async transferPipeline(row) {
      await axios.post(this.userRoute + "/pipelines/transfer", {
        pipeline_id: row.item.id
      })
      .then(response => {
        this.$emit('getItems');
        swal.fire({
          title: 'Transferred!',
          text: response.data,
          type: 'success',
          showConfirmButton: false,
          timer: 1500,
          icon: 'success'
        });
      })
      .catch(error => {
        this.error(error);
      });
    },
    checkParams() {
      this.filters.type = this.$route.query.type ?? '';
      this.filters.status = this.$route.query.status ?? '';
      this.filters.transferred = this.$route.query.transferred ?? false;
      this.searchQuery = this.$route.query.search ?? '';
    }
  },
  watch: {
    data(to, from) {
      this.tableData = to;
    },
    'filters.type'(to, from) {
      this.$emit('updateType', to);
    },
    'filters.status'(to, from) {
      this.$emit('updateStatus', to);
    },
    'filters.transferred'(to, from) {
      this.$emit('updateTransferred', to);
    },
    searchQuery(to, from) {
      if (!this.$route.query.search || this.$route.query.search !== to) {
          this.$router.replace({path: this.$route.path, query: {...this.$route.query, search: to}});
      }
    }
  },
  created() {
    this.checkParams();
  }
}
</script>
<style>
  .no-border-card .card-footer{
    border-top: 0;
  }
</style>